import {Form, Formik, FormikProps} from 'formik';
import {get, isEmpty} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Accordion,
    AccordionDetails,
    AccordionTitle,
    Alert,
    Button,
    DataWrapper,
    MultiInputFormik,
    UnsavedChangesLeavePrompt
} from 'components';
import {Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

import {LiveHelpMessageInput} from '../LiveHelpMessageInput';
import {FIELDS} from '../constants';
import {AutoReplySections, LiveHelpAutoReplyFields, MessageItem, MessageType} from '../types';
import {liveHelpUtils} from '../utils';

type LiveHelpAutoReplyProps = {
    loading: boolean;
    initialValues: LiveHelpAutoReplyFields;
    interactiveChatOptions: Option[];
    messageTypeOptions: Option<MessageType>[];
    onSubmit: (values: LiveHelpAutoReplyFields) => void;
};

type FormTemplateProps = {
    loading: boolean;
    initialValues: LiveHelpAutoReplyFields;
    interactiveChatOptions: Option[];
    messageTypeOptions: Option<MessageType>[];
} & FormikProps<LiveHelpAutoReplyFields>;

const T_KEYS_LIVE_HELP = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.LIVE_HELP;
const T_KEYS = T_KEYS_LIVE_HELP.AUTO_REPLY;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

const FormTemplate: React.FC<FormTemplateProps> = ({
    loading, initialValues, messageTypeOptions, interactiveChatOptions, submitForm, values, errors,
}) => {
    const {t} = useTranslation();

    const validateAgentsAvailableItem = useCallback((item: MessageItem) =>
        liveHelpUtils.validateItem(item, values.agentsAvailable), [values]);

    const validateAgentsUnavailableItem = useCallback((item: MessageItem) =>
        liveHelpUtils.validateItem(item, values.agentsUnavailable), [values]);

    const hasNoChanges = useMemo(() => {
        const noChangesAgentsAvailable = liveHelpUtils.hasNoChanges(values.agentsAvailable, initialValues.agentsAvailable);
        const noChangesAgentsUnavailable = liveHelpUtils.hasNoChanges(values.agentsUnavailable, initialValues.agentsUnavailable);
        return noChangesAgentsAvailable && noChangesAgentsUnavailable;
    }, [initialValues, values]);
    const interactiveChatPositionWarning = useMemo(() =>
        get(errors?.agentsAvailable?.messages, 'interactiveChatPosition') ||
        get(errors?.agentsUnavailable?.messages, 'interactiveChatPosition'), [errors]);

    return (
        <Form className='tw-relative'>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Accordion className='tw-my-4' defaultExpanded>
                    <AccordionTitle>
                        <div className='tw-flex tw-justify-between tw-font-bold tw-text-lg'>
                            {t(T_KEYS.TITLE)}
                        </div>
                    </AccordionTitle>
                    <AccordionDetails>
                        <div className='tw-text-default tw-mt-4'>
                            {t(T_KEYS.DESCRIPTION)}
                        </div>
                        <div className='tw-ts-base-bold tw-mb-4 tw-mt-6'>
                            {t(T_KEYS.AGENTS_AVAILABLE.TITLE)}
                        </div>
                        <MultiInputFormik
                            name={`${AutoReplySections.AgentsAvailable}.${FIELDS.MESSAGES}`}
                            itemsPath={`${AutoReplySections.AgentsAvailable}.${FIELDS.MESSAGES}`}
                            defaultValue={{type: MessageType.Text, text: ''}}
                            values={values}
                            isValid={validateAgentsAvailableItem}
                        >
                            {({index, item, path, last, renderActions}) => (
                                <div key={index} className='tw-flex tw-full tw-items-center tw-mb-controls'>
                                    <LiveHelpMessageInput
                                        item={item}
                                        path={path}
                                        last={last}
                                        interactiveChatOptions={interactiveChatOptions}
                                        messageTypeOptions={messageTypeOptions}
                                    />
                                    <div className='tw-w-10'>{renderActions({index, item})}</div>
                                </div>
                            )}
                        </MultiInputFormik>
                        <div className='tw-ts-base-bold tw-mb-4 tw-mt-6'>
                            {t(T_KEYS.AGENTS_UNAVAILABLE.TITLE)}
                        </div>
                        <MultiInputFormik
                            name={`${AutoReplySections.AgentsUnavailable}.${FIELDS.MESSAGES}`}
                            itemsPath={`${AutoReplySections.AgentsUnavailable}.${FIELDS.MESSAGES}`}
                            defaultValue={{type: MessageType.Text, text: ''}}
                            values={values}
                            isValid={validateAgentsUnavailableItem}
                        >
                            {({index: controlIndex, item, path, last, renderActions}) => (
                                <div key={controlIndex} className='tw-flex tw-full tw-items-end tw-mb-controls'>
                                    <LiveHelpMessageInput
                                        item={item}
                                        path={path}
                                        last={last}
                                        interactiveChatOptions={interactiveChatOptions}
                                        messageTypeOptions={messageTypeOptions}
                                    />
                                    <div className='tw-w-10'>{renderActions({index: controlIndex, item})}</div>
                                </div>
                            )}
                        </MultiInputFormik>
                        {interactiveChatPositionWarning &&
                            <Alert type='warning' className='tw-my-4'>{t(T_KEYS_LIVE_HELP.INTERACTIVE_CHAT_ITEM_NOT_LAST_WARNING)}</Alert>}
                        <div className='tw-text-right tw-my-4'>
                            <Button
                                color='primary'
                                disabled={hasNoChanges || !isEmpty(errors)}
                                loading={loading}
                                onClick={submitForm}
                            >
                                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                            </Button>
                        </div>
                    </AccordionDetails>
                </Accordion>
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

export const LiveHelpAutoReply: React.FC<LiveHelpAutoReplyProps> = ({
    loading, initialValues, messageTypeOptions, interactiveChatOptions, onSubmit
}) => {
    const {t} = useTranslation();

    const validate = useCallback((values: LiveHelpAutoReplyFields) => {
        const agentsAvailableErrors = liveHelpUtils.validateMessages(values.agentsAvailable.messages, t);
        const agentsUnavailableErrors = liveHelpUtils.validateMessages(values.agentsUnavailable.messages, t);
        return {
            ...(!isEmpty(agentsAvailableErrors) && {agentsAvailable: agentsAvailableErrors}),
            ...(!isEmpty(agentsUnavailableErrors) && {agentsUnavailable: agentsUnavailableErrors}),
        };
    }, [t]);

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            validate={validate}
            validateOnChange
            validateOnBlur
            onSubmit={onSubmit}
        >
            {(props) => (
                <FormTemplate
                    {...props}
                    loading={loading}
                    interactiveChatOptions={interactiveChatOptions}
                    messageTypeOptions={messageTypeOptions}
                />
            )}
        </Formik>
    );
};